import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Minus,
  Plus,
  BadgeCheck,
  Search,
  Wallet,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  BarChart3,
  ClipboardCheck,
  PhoneCall,
} from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import useReveal from "../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Shared --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({ children, testid, fullWidth = false }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all ${
      fullWidth ? "w-full" : ""
    }`}
  >
    {children}
    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
);

const OutlineCTA = ({ children, testid, fullWidth = false }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-lg bg-white text-[#0A1628] border border-slate-300 hover:border-[#0A1628] px-6 py-3.5 text-[15px] font-semibold transition-colors ${
      fullWidth ? "w-full" : ""
    }`}
  >
    {children}
  </a>
);

/* ---------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section
    data-testid="pricing-hero"
    className="relative pt-32 lg:pt-40 pb-16 lg:pb-20 overflow-hidden"
  >
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[460px] w-[460px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8 text-center reveal">
      <Eyebrow>Pricing</Eyebrow>
      <h1 className="h1-hero mt-5 text-[#0A1628]">
        Flat monthly pricing.
        <br />
        <span className="text-[#16A06A]">No hourly surprises.</span>
      </h1>
      <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-3xl mx-auto">
        Four plans built from the same five operating pillars. Pick the depth
        your business needs today — move up when you outgrow it.
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {[
          "No setup fees",
          "Cancel with 30 days' notice",
          "Renews monthly",
        ].map((c) => (
          <div key={c} className="inline-flex items-center gap-2 text-[14px] text-slate-600">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E7F6EE]">
              <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
            </span>
            {c}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------- Plans ---------------------------------- */

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    price: "$1,500",
    per: "/mo",
    capacity: "20 hrs/week",
    features: [
      "Post-call recap docs (4/mo)",
      "QuickBooks export cleanup (1×/mo)",
      "Agenda prep & follow-up emails",
      "Google Drive file management",
    ],
    bestFor: "Solo operator, 1–3 people",
    cta: "Book a Scope Call",
    ctaTone: "outline",
  },
  {
    key: "growth",
    name: "Growth",
    price: "$2,750",
    per: "/mo",
    capacity: "40 hrs/week",
    features: [
      "Everything in Starter",
      "KPI dashboard build & maintenance",
      "CRM (GoHighLevel) setup & lead pipeline",
      "Google LSA & Business Profile management",
      "Monthly P&L report prep",
    ],
    bestFor: "5–15 people, $750K–$2M revenue",
    cta: "Book a Scope Call",
    ctaTone: "primary",
    popular: true,
  },
  {
    key: "operator",
    name: "Operator",
    price: "$4,500",
    per: "/mo",
    capacity: "60 hrs/week",
    features: [
      "Everything in Growth",
      "Dedicated, named operator",
      "Pricing & job costing support",
      "HR admin & onboarding docs",
      "Commission plan documentation",
      "Weekly ops summary report",
    ],
    bestFor: "15+ employees, scaling past $2M",
    cta: "Book a Scope Call",
    ctaTone: "outline",
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Custom",
    per: "",
    capacity: "Dedicated team",
    features: [
      "2+ operators assigned",
      "White-glove onboarding",
      "Custom SOP development",
      "Direct Slack / project-management access",
      "Monthly strategy review call",
    ],
    bestFor: "Multi-location or $5M+ operator",
    cta: "Talk to Us",
    ctaTone: "outline",
  },
];

const Plans = () => (
  <section data-testid="pricing-plans" className="py-16 lg:py-20">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 lg:items-stretch">
        {PLANS.map((p, i) => (
          <article
            key={p.key}
            data-testid={`plan-${p.key}`}
            className={`reveal relative flex flex-col rounded-2xl p-6 lg:p-7 card-soft transition-all ${
              p.popular
                ? "bg-white border-2 border-[#16A06A] lg:scale-[1.03] lg:-translate-y-1 shadow-[0_18px_50px_rgba(22,160,106,0.20)]"
                : "bg-white border border-slate-200 hover:-translate-y-1 hover:border-[#16A06A]/40"
            }`}
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            {p.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-[#16A06A] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 shadow-[0_4px_14px_rgba(22,160,106,0.35)]">
                Most Popular
              </span>
            )}
            <h3 className="text-[19px] font-bold text-[#0A1628]">{p.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-[34px] font-bold tracking-tight text-[#0A1628] leading-none">
                {p.price}
              </span>
              {p.per && (
                <span className="text-[15px] font-medium text-slate-400">{p.per}</span>
              )}
            </div>
            <div className="mt-1.5 text-[13px] font-semibold text-slate-500">
              {p.capacity}
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <ul className="space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[14px] text-slate-600">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F6EE]">
                      <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                    </span>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100 text-[12px] uppercase tracking-wider font-semibold text-slate-400">
              Best for
            </div>
            <p className="mt-1 text-[13px] text-slate-500 leading-relaxed flex-1">
              {p.bestFor}
            </p>

            <div className="mt-6">
              {p.ctaTone === "primary" ? (
                <PrimaryCTA testid={`plan-${p.key}-cta`} fullWidth>
                  {p.cta}
                </PrimaryCTA>
              ) : (
                <OutlineCTA testid={`plan-${p.key}-cta`} fullWidth>
                  {p.cta}
                </OutlineCTA>
              )}
            </div>
          </article>
        ))}
      </div>

      <p className="reveal mt-8 text-center text-[13px] text-slate-400">
        Retainers renew monthly. Cancel with 30 days' notice. No setup fees.
      </p>
    </div>
  </section>
);

/* ------------------------------ Pillar Matrix ----------------------------- */

const PILLARS = [
  {
    n: "01",
    title: "Financial Admin",
    desc: "QuickBooks cleanup, P&L formatting, breakeven & variance tracking",
    row: ["partial", "full", "full", "full-custom"],
    partialLabel: "QB export cleanup only",
  },
  {
    n: "02",
    title: "Call Operations",
    desc: "Post-call recap docs, follow-up email drafting, agenda prep",
    row: ["full", "full", "full", "full-custom"],
  },
  {
    n: "03",
    title: "CRM & Lead Ops",
    desc: "GoHighLevel/HubSpot setup, Google LSA & GBP, lead pipeline",
    row: ["none", "full", "full", "full-custom"],
  },
  {
    n: "04",
    title: "Pricing Support",
    desc: "Estimate sheets, job cost templates, margin dashboards",
    row: ["none", "none", "full", "full-custom"],
  },
  {
    n: "05",
    title: "HR & People Admin",
    desc: "Offer letters, onboarding checklists, commission plan docs",
    row: ["none", "none", "full", "full-custom"],
  },
];

const MATRIX_PLANS = ["Starter", "Growth", "Operator", "Enterprise"];

const CellState = ({ state, partialLabel }) => {
  if (state === "full") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#16A06A] text-white">
          <Check size={16} strokeWidth={3} />
        </span>
        <span className="text-[12px] font-semibold text-[#0A1628]">Included</span>
      </div>
    );
  }
  if (state === "full-custom") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#16A06A] text-white">
          <Check size={16} strokeWidth={3} />
        </span>
        <span className="text-[12px] font-semibold text-[#0A1628]">Included + custom</span>
      </div>
    );
  }
  if (state === "partial") {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#E7F6EE] text-[#16A06A] border border-[#16A06A]/30">
          <Minus size={16} strokeWidth={3} />
        </span>
        <span className="text-[12px] font-semibold text-[#16A06A] text-center leading-tight">
          Partial
          {partialLabel ? (
            <>
              <br />
              <span className="text-[11px] font-normal text-slate-500">{partialLabel}</span>
            </>
          ) : null}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <span className="block h-[2px] w-3.5 bg-slate-300 rounded" />
      </span>
      <span className="text-[12px] font-medium text-slate-400">—</span>
    </div>
  );
};

const Matrix = () => (
  <section data-testid="pricing-matrix" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">What Each Plan Unlocks</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Five pillars. Four plans. One clear ladder.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Every plan is built from the same five operating pillars — you unlock
          more of them as you grow.
        </p>
      </div>

      <div
        className="reveal mt-12 rounded-2xl bg-white border border-slate-200 card-soft overflow-hidden"
        data-testid="pricing-matrix-table"
      >
        <div className="overflow-x-auto">
          <table className="min-w-[820px] w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-white px-5 md:px-6 py-5 text-[12px] md:text-[13px] font-semibold uppercase tracking-wider text-slate-500 min-w-[260px]">
                  Pillar
                </th>
                {MATRIX_PLANS.map((p) => {
                  const isGrowth = p === "Growth";
                  return (
                    <th
                      key={p}
                      className={`px-5 md:px-6 py-5 text-center text-[13px] md:text-[14px] font-bold text-[#0A1628] ${
                        isGrowth ? "bg-[#E7F6EE]/70" : ""
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{p}</span>
                        {isGrowth && (
                          <span className="inline-flex items-center rounded-full bg-[#16A06A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                            Most Popular
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {PILLARS.map((row) => (
                <tr key={row.n} className="border-t border-slate-200">
                  <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-6 align-top min-w-[260px] border-r border-slate-100">
                    <div className="flex items-baseline gap-3">
                      <span className="text-[15px] font-bold text-[#16A06A]">{row.n}</span>
                      <div>
                        <div className="text-[15px] font-semibold text-[#0A1628]">
                          {row.title}
                        </div>
                        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
                          {row.desc}
                        </p>
                      </div>
                    </div>
                  </td>
                  {row.row.map((state, i) => {
                    const isGrowth = i === 1;
                    return (
                      <td
                        key={i}
                        className={`px-5 md:px-6 py-6 text-center ${
                          isGrowth ? "bg-[#E7F6EE]/70" : ""
                        }`}
                      >
                        <CellState
                          state={state}
                          partialLabel={i === 0 ? row.partialLabel : undefined}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Summary rows */}
              <tr className="border-t-2 border-slate-300">
                <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-5 text-[14px] font-semibold text-[#0A1628] border-r border-slate-100">
                  Dedicated named operator
                </td>
                {["—", "—", "check", "2+"].map((v, i) => {
                  const isGrowth = i === 1;
                  return (
                    <td
                      key={i}
                      className={`px-5 md:px-6 py-5 text-center ${
                        isGrowth ? "bg-[#E7F6EE]/70" : ""
                      }`}
                    >
                      {v === "check" ? (
                        <Check
                          size={18}
                          className="mx-auto text-[#16A06A]"
                          strokeWidth={3}
                        />
                      ) : v === "2+" ? (
                        <span className="text-[14px] font-semibold text-[#16A06A]">
                          ✓ 2+ operators
                        </span>
                      ) : (
                        <span className="text-[15px] text-slate-400">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-t border-slate-200">
                <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-5 text-[14px] font-semibold text-[#0A1628] border-r border-slate-100">
                  Weekly capacity
                </td>
                {["20 hrs", "40 hrs", "60 hrs", "Custom"].map((v, i) => {
                  const isGrowth = i === 1;
                  return (
                    <td
                      key={v}
                      className={`px-5 md:px-6 py-5 text-center text-[14px] font-semibold text-[#0A1628] ${
                        isGrowth ? "bg-[#E7F6EE]/70" : ""
                      }`}
                    >
                      {v}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="reveal mt-5 text-[12px] text-slate-400 max-w-4xl">
        Hours shown are planned capacity, not a billing meter. You're buying
        scoped deliverables at a flat monthly fee.
      </p>
    </div>
  </section>
);

/* ------------------------------- How It Works ----------------------------- */

const Node = ({ title, sub, accent = "ink", testid }) => (
  <div
    data-testid={testid}
    className={`flex-1 rounded-2xl border card-soft p-6 lg:p-7 text-center ${
      accent === "green"
        ? "bg-[#E7F6EE] border-[#16A06A]/20"
        : "bg-white border-slate-200"
    }`}
  >
    <div
      className={`mx-auto h-11 w-11 rounded-full flex items-center justify-center text-white ${
        accent === "green" ? "bg-[#16A06A]" : "bg-[#0A1628]"
      }`}
    >
      <BadgeCheck size={18} />
    </div>
    <div className="mt-3 text-[16px] font-bold text-[#0A1628]">{title}</div>
    <div className="mt-1 text-[13px] text-slate-500">{sub}</div>
  </div>
);

const Arrow = ({ label, direction = "right" }) => (
  <div className="flex items-center justify-center md:flex-col text-[#16A06A]">
    <div className="hidden md:flex flex-col items-center">
      <span className="eyebrow text-[#16A06A] whitespace-nowrap">{label}</span>
      <div className="mt-2">
        <ArrowRight
          size={26}
          strokeWidth={2.5}
          className={direction === "right" ? "" : "-scale-x-100"}
        />
      </div>
    </div>
    <div className="md:hidden flex flex-col items-center py-3">
      <span className="eyebrow text-[#16A06A]">{label}</span>
      <div className="mt-1 rotate-90">
        <ArrowRight
          size={22}
          strokeWidth={2.5}
          className={direction === "right" ? "" : "-scale-x-100"}
        />
      </div>
    </div>
  </div>
);

const YOU_CONTROL = [
  "Priorities and deadlines",
  "What gets worked on",
  "Access to your tools and accounts",
  "Quality standards",
];
const WE_HANDLE = [
  "Recruiting and vetting",
  "Employment, payroll and benefits",
  "Training on trades workflows",
  "Quality assurance and backup coverage",
  "All the compliance paperwork",
];

const HowItWorks = () => (
  <section data-testid="pricing-how" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">How It Works</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">We employ. You direct.</h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          You get a trained operator working inside your business — without
          payroll, contracts, hiring, or HR admin landing on your desk. We
          carry the employment. You set the priorities.
        </p>
      </div>

      <div className="reveal mt-12 rounded-2xl bg-white border border-slate-200 card-soft p-7 lg:p-10">
        <div className="flex flex-col md:flex-row md:items-stretch md:gap-4">
          <Node testid="how-node-owner" title="You" sub="Owner / Operator" />
          <Arrow label="Directs the work" direction="right" />
          <Node
            testid="how-node-operator"
            accent="green"
            title="Your RevGen Operator"
            sub="Works inside your tools"
          />
          <Arrow label="Employs · Trains · Pays" direction="left" />
          <Node testid="how-node-revgen" title="RevGen" sub="Employment & training" />
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-5">
        <div className="reveal rounded-2xl bg-white border border-slate-200 card-soft p-7">
          <Eyebrow tone="muted">You control</Eyebrow>
          <ul className="mt-5 space-y-3">
            {YOU_CONTROL.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] text-[#0A1628]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="reveal rounded-2xl bg-[#E7F6EE] border border-[#16A06A]/20 p-7">
          <Eyebrow tone="muted">RevGen handles</Eyebrow>
          <ul className="mt-5 space-y-3">
            {WE_HANDLE.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] text-[#0A1628]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#16A06A] text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------ Projects ---------------------------------- */

const PROJECTS = [
  {
    title: "Breakeven Dashboard Build",
    desc:
      "Full breakeven model, KPI inputs, and dashboard from your QuickBooks export.",
    price: "$750",
    turnaround: "3–5 days",
  },
  {
    title: "Financial Review (One-Time)",
    desc:
      "P&L cleanup, chart-of-accounts reclassification, gross-margin recalculation, and summary report.",
    price: "$600",
    turnaround: "2–4 days",
  },
  {
    title: "SOP Documentation Package",
    desc: "2–3 core processes documented — invoicing, dispatch, job closeout.",
    price: "$500",
    turnaround: "3–5 days",
  },
  {
    title: "Commission Plan Build",
    desc:
      "Role-specific comp plan with tiers, clawback logic, and worked example math.",
    price: "$400",
    turnaround: "2–3 days",
  },
  {
    title: "CRM / GoHighLevel Setup",
    desc:
      "Pipeline build, lead stages, follow-up automation, and Google LSA integration.",
    price: "$850",
    turnaround: "5–7 days",
  },
  {
    title: "Google LSA + Business Profile Launch",
    desc:
      "Account setup, category optimization, review strategy, and budget structure.",
    price: "$500",
    turnaround: "3–5 days",
  },
];

const Projects = () => (
  <section
    id="projects"
    data-testid="pricing-projects"
    className="py-24 lg:py-28 bg-[#F8FAFC] scroll-mt-24"
  >
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Projects</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Not ready for a monthly plan? Start with a project.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Fixed fee, defined scope, clear deliverable. Ideal for a first
          engagement or add-on work outside your plan.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {PROJECTS.map((p, i) => (
          <article
            key={p.title}
            data-testid={`project-${i + 1}`}
            className="reveal group flex flex-col bg-white rounded-2xl border border-slate-200 card-soft p-7 hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <h3 className="text-[18px] font-semibold text-[#0A1628] leading-snug">
              {p.title}
            </h3>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-500 flex-1">
              {p.desc}
            </p>
            <div className="mt-6 flex items-baseline justify-between gap-3 pt-5 border-t border-slate-100">
              <span className="text-[28px] font-bold tracking-tight text-[#16A06A] leading-none">
                {p.price}
              </span>
              <span className="text-[12px] uppercase tracking-wider font-semibold text-slate-400">
                {p.turnaround}
              </span>
            </div>
          </article>
        ))}
      </div>

      <div
        data-testid="projects-credit-callout"
        className="reveal mt-10 rounded-2xl bg-[#E7F6EE] border border-[#16A06A]/20 p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <p className="text-[15px] lg:text-[16px] font-semibold text-[#0A1628]">
          Every project fee is credited toward your first month if you move to
          a monthly plan within 30 days.
        </p>
        <PrimaryCTA testid="projects-cta">Book a Scope Call</PrimaryCTA>
      </div>
    </div>
  </section>
);

/* ------------------------- Every plan includes ---------------------------- */

const INCLUDES = [
  {
    icon: Search,
    title: "Recruiting & vetting",
    body: "We find and screen the person. You never post a job.",
  },
  {
    icon: Wallet,
    title: "Employment, payroll & benefits",
    body: "Carried entirely by RevGen. One invoice to you.",
  },
  {
    icon: GraduationCap,
    title: "Trades-specific training",
    body:
      "Your operator knows QuickBooks, ServiceTitan and GoHighLevel before day one.",
  },
  {
    icon: BookOpen,
    title: "Your own SOP library",
    body: "Every process we run is documented and belongs to you.",
  },
  {
    icon: ShieldCheck,
    title: "Backup coverage",
    body: "Wrong fit or turnover — replaced within two weeks.",
  },
  {
    icon: BarChart3,
    title: "Quality assurance",
    body: "A RevGen lead reviews output before it reaches you.",
  },
  {
    icon: ClipboardCheck,
    title: "Onboarding support",
    body: "We set up access, tools, and workflows with you.",
  },
  {
    icon: PhoneCall,
    title: "A named point of contact",
    body: "One person you call. Not a ticket queue.",
  },
];

const EveryPlan = () => (
  <section data-testid="pricing-includes" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Every Plan Includes</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          The parts nobody quotes you on.
        </h2>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {INCLUDES.map((it, i) => {
          const Icon = it.icon;
          return (
            <article
              key={it.title}
              data-testid={`includes-${i + 1}`}
              className="reveal bg-white rounded-2xl border border-slate-200 card-soft p-5"
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={18} strokeWidth={2.2} />
              </div>
              <h3 className="mt-4 text-[15px] font-semibold text-[#0A1628] leading-snug">
                {it.title}
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-500">
                {it.body}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------- Value math ------------------------------- */

const VALUE_HEAD = ["Doing it yourself", "Hiring in-house", "RevGen Growth"];
const VALUE_ROWS = [
  ["Monthly cost", '"Free" — 40+ of your hours', "$4,000–6,000 fully loaded", "$2,750 flat"],
  ["Time to productive", "Immediate — and never ends", "6–10 weeks", "2–3 weeks"],
  ["Trades training", "You already know it", "You have to teach it", "Included"],
  ["Coverage if they leave", "N/A", "You start over", "Handled by us"],
  ["Payroll, benefits, HR admin", "N/A", "Yours", "Ours"],
  ["Documented SOPs", "In your head", "Rarely", "Always"],
];

const ValueMath = () => (
  <section data-testid="pricing-value" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Math</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          What 10 hours a week is actually costing you.
        </h2>
      </div>

      <div className="reveal mt-12 overflow-x-auto rounded-2xl bg-white border border-slate-200 card-soft">
        <table className="min-w-[760px] w-full text-left">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-5 md:px-8 py-5 text-[12px] md:text-[13px] font-semibold uppercase tracking-wider text-slate-500">
                &nbsp;
              </th>
              {VALUE_HEAD.map((h, i) => {
                const isRev = i === 2;
                return (
                  <th
                    key={h}
                    className={`px-5 md:px-8 py-5 text-[12px] md:text-[13px] font-semibold uppercase tracking-wider ${
                      isRev ? "text-[#16A06A] bg-[#E7F6EE]" : "text-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {h}
                      {isRev && (
                        <span className="inline-flex items-center rounded-full bg-[#16A06A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                          Recommended
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {VALUE_ROWS.map((row) => (
              <tr
                key={row[0]}
                className="border-t border-slate-200 text-[14px] md:text-[15px]"
              >
                <td className="px-5 md:px-8 py-5 md:py-6 font-semibold text-[#0A1628]">
                  {row[0]}
                </td>
                <td className="px-5 md:px-8 py-5 md:py-6 text-slate-600">{row[1]}</td>
                <td className="px-5 md:px-8 py-5 md:py-6 text-slate-600">{row[2]}</td>
                <td className="px-5 md:px-8 py-5 md:py-6 text-[#0A1628] font-medium bg-[#E7F6EE]/70">
                  {row[3]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="reveal mt-5 text-[12px] leading-relaxed text-slate-400 max-w-4xl">
        Fully-loaded in-house figure reflects a US administrative/office-manager
        salary plus payroll taxes, benefits, software, and management overhead.
        Illustrative ranges — your actual numbers are reviewed on the scope
        call.
      </p>

      <div className="reveal mt-6">
        <Link
          to="/calculator"
          data-testid="pricing-run-numbers-link"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[14px] font-semibold text-[#0A1628] hover:border-[#16A06A] hover:text-[#16A06A] transition-colors"
        >
          Run your own numbers → <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </section>
);

/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    q: "What am I actually paying for?",
    a: "A flat monthly fee for a scoped set of deliverables across your plan's pillars. Not an hourly rate, not a headcount cost. The hours shown are planned capacity so you know what a plan can absorb.",
  },
  {
    q: "What if I need more than my plan covers?",
    a: "Two options: move up a plan, or add a fixed-fee project. We'll flag it before doing the work — you'll never see a surprise line item.",
  },
  {
    q: "Are there setup fees?",
    a: "No. Onboarding is included in every plan.",
  },
  {
    q: "How long am I committed?",
    a: "Retainers renew monthly and cancel with 30 days' notice.",
  },
  {
    q: "Can I switch plans?",
    a: "Yes, at any renewal. Most owners start on Starter or a project and move to Growth once the KPI and CRM work becomes the priority.",
  },
  {
    q: "Do project fees apply toward a plan?",
    a: "Yes. Any project fee is credited toward your first month if you start a monthly plan within 30 days.",
  },
  {
    q: "Is the Enterprise price really custom?",
    a: "Yes. Multi-location and $5M+ operators need multiple operators and custom SOPs, so we scope and quote it directly.",
  },
  {
    q: "Who employs my operator?",
    a: "RevGen does. We recruit, employ, train, and pay them. You direct their work. That's what the flat fee covers.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="pricing-faq" className="py-24 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <Eyebrow tone="muted">FAQ</Eyebrow>
            <h2 className="h2-section mt-3 text-[#0A1628]">Pricing questions.</h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Still unsure?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
              >
                Book a free assessment →
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 reveal">
            <div className="rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q} data-testid={`pricing-faq-item-${i + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`pricing-faq-toggle-${i + 1}`}
                      className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">
                        {f.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen
                            ? "bg-[#16A06A] text-white"
                            : "bg-[#E7F6EE] text-[#16A06A]"
                        }`}
                      >
                        {isOpen ? (
                          <Minus size={16} strokeWidth={3} />
                        ) : (
                          <Plus size={16} strokeWidth={3} />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA ------------------------------- */

const FinalCTA = () => (
  <section
    data-testid="pricing-final-cta"
    className="relative bg-[#0A1628] overflow-hidden"
  >
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Get started</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Not sure which plan you need?
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a free 30-minute back office assessment. We'll score where your time
          is going and tell you which plan fits — or if a project is the
          smarter start.
        </p>
        <div className="mt-9 flex justify-center">
          <PrimaryCTA testid="pricing-final-cta-btn">
            Get a Free Back Office Assessment
          </PrimaryCTA>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function Pricing() {
  useReveal();

  useEffect(() => {
    document.title = "Pricing | RevGen Virtual Operations for Trades";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Flat monthly plans from $1,500/mo for trades and home services back office support — bookkeeping, CRM, call ops, job costing and HR admin. Fixed-fee projects from $400."
      );
    }
    // Only jump to top when landing without a hash (preserve #projects anchor).
    if (!window.location.hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  return (
    <div data-testid="pricing-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Plans />
        <Matrix />
        <HowItWorks />
        <Projects />
        <EveryPlan />
        <ValueMath />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
